import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFooter, updateFooter } from '../store/slices/footerSlice';
import { RootState, AppDispatch } from '../store';
import { PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const FooterManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { footer, isLoading } = useSelector((state: RootState) => state.footer);
  
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    address: '',
    mobileno: '',
    instagramlink: '',
    facebooklink: '',
    twitterlink: '',
    description: '',
  });

  useEffect(() => {
    dispatch(fetchFooter());
  }, [dispatch]);

  useEffect(() => {
    if (footer) {
      setFormData({
        address: footer.address || '',
        mobileno: footer.mobileno || '',
        instagramlink: footer.instagramlink || '',
        facebooklink: footer.facebooklink || '',
        twitterlink: footer.twitterlink || '',
        description: footer.description || '',
      });
    }
  }, [footer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await dispatch(updateFooter(formData)).unwrap();
      toast.success('Footer updated successfully');
      setShowModal(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update footer');
    }
  };

  const handleEdit = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      address: footer.address || '',
      mobileno: footer.mobileno || '',
      instagramlink: footer.instagramlink || '',
      facebooklink: footer.facebooklink || '',
      twitterlink: footer.twitterlink || '',
      description: footer.description || '',
    });
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Footer Management</h1>
          <p className="mt-2 text-sm text-gray-700">Manage footer contact information</p>
        </div>
        <button
          onClick={handleEdit}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
        >
          <PencilIcon className="h-5 w-5 mr-2" />
          Edit Footer
        </button>
      </div>

      {/* Footer Info Display */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Current Footer Information</h3>
        </div>
        
        {isLoading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700">Description</label>
              <p className="mt-1 text-sm text-gray-900">{footer.description || 'Not set'}</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">Address</label>
              <p className="mt-1 text-sm text-gray-900">{footer.address || 'Not set'}</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">Mobile Number</label>
              <p className="mt-1 text-sm text-gray-900">{footer.mobileno || 'Not set'}</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">Instagram Link</label>
              {footer.instagramlink ? (
                <a href={footer.instagramlink} target="_blank" rel="noopener noreferrer" className="mt-1 text-sm text-blue-600 hover:text-blue-800 underline">
                  {footer.instagramlink}
                </a>
              ) : (
                <p className="mt-1 text-sm text-gray-900">Not set</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">Facebook Link</label>
              {footer.facebooklink ? (
                <a href={footer.facebooklink} target="_blank" rel="noopener noreferrer" className="mt-1 text-sm text-blue-600 hover:text-blue-800 underline">
                  {footer.facebooklink}
                </a>
              ) : (
                <p className="mt-1 text-sm text-gray-900">Not set</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">Twitter Link</label>
              {footer.twitterlink ? (
                <a href={footer.twitterlink} target="_blank" rel="noopener noreferrer" className="mt-1 text-sm text-blue-600 hover:text-blue-800 underline">
                  {footer.twitterlink}
                </a>
              ) : (
                <p className="mt-1 text-sm text-gray-900">Not set</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Edit Footer Information</h3>
                    <button type="button" onClick={handleCloseModal}>
                      <XMarkIcon className="h-6 w-6 text-gray-400" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <textarea
                        rows={4}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        placeholder="Enter footer description"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <textarea
                        rows={3}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        placeholder="Enter address"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Mobile Number</label>
                      <input
                        type="text"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.mobileno}
                        onChange={(e) => setFormData({...formData, mobileno: e.target.value})}
                        placeholder="Enter mobile number"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Instagram Link</label>
                      <input
                        type="url"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.instagramlink}
                        onChange={(e) => setFormData({...formData, instagramlink: e.target.value})}
                        placeholder="Enter Instagram URL"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Facebook Link</label>
                      <input
                        type="url"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.facebooklink}
                        onChange={(e) => setFormData({...formData, facebooklink: e.target.value})}
                        placeholder="Enter Facebook URL"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Twitter Link</label>
                      <input
                        type="url"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.twitterlink}
                        onChange={(e) => setFormData({...formData, twitterlink: e.target.value})}
                        placeholder="Enter Twitter URL"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {isLoading ? 'Updating...' : 'Update'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FooterManagement;